import { cva } from "../../lib";

// No `group` here: on native, NativeWind turns a View with `group` into a
// Pressable, so RadioIndicator would take the tap from the row or card around
// it. RadioButton adds `group` on its own Pressable.
export const radioButtonVariants = cva(
  [
    "rounded-radius-full flex aspect-square shrink-0 items-center justify-center border-solid",
    "border-luce-neutral-border-control border-1-5 outline-none transition-colors",
    "web:outline-none web:transition-colors",
    "web:focus-visible:ring-focus",
    "web:focus-visible:ring-luce-brand-primaryBorder",
    "web:focus-visible:ring-offset-focus",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-radio-sm w-radio-sm md:h-radio-sm-dt md:w-radio-sm-dt",
        md: "h-radio-md w-radio-md md:h-radio-md-dt md:w-radio-md-dt",
      },
      indicator: {
        fill: "",
        check: "",
      },
      checked: {
        true: "border-luce-brand-primary",
        false: "",
      },
      disabled: {
        true: "pointer-events-none",
        false: "",
      },
      // RadioButton is the control and hovers itself; a RadioIndicator follows
      // the row or card around it (`group-hover:`), so a card without `group`
      // shows no hover rather than half of one
      self: {
        true: "",
        false: "",
      },
      // A RadioIndicator rings when the row or card around it (with `group`)
      // has keyboard focus; RadioButton rings itself (focus-visible above)
      groupFocus: {
        true: [
          "web:group-focus-visible:ring-focus",
          "web:group-focus-visible:ring-luce-brand-primaryBorder",
          "web:group-focus-visible:ring-offset-focus",
        ].join(" "),
        false: "",
      },
    },
    compoundVariants: [
      // Hover (web), as Checkbox's: a brand/primary halo at 10%, 2px outside the
      // circle (Figma 68:9212, 68:9201); unchecked, the border stays
      // border-control, as Figma's Hovered binds it. A keyboard focus ring
      // comes later and wins.
      {
        indicator: "fill",
        disabled: false,
        checked: false,
        class: [
          "web:group-hover:bg-luce-neutral-background-default",
          "web:group-hover:ring-halo web:group-hover:ring-luce-brand-primary/10",
        ].join(" "),
      },
      {
        indicator: "fill",
        disabled: false,
        checked: false,
        self: true,
        class: [
          "web:hover:bg-luce-neutral-background-default",
          "web:hover:ring-halo web:hover:ring-luce-brand-primary/10",
        ].join(" "),
      },
      {
        indicator: "fill",
        disabled: false,
        checked: true,
        class: [
          "web:group-hover:border-luce-brand-primaryHover",
          "web:group-hover:ring-halo web:group-hover:ring-luce-brand-primary/10",
        ].join(" "),
      },
      {
        indicator: "fill",
        disabled: false,
        checked: true,
        self: true,
        class:
          "web:hover:border-luce-brand-primaryHover web:hover:ring-halo web:hover:ring-luce-brand-primary/10",
      },
      {
        indicator: "fill",
        disabled: true,
        checked: false,
        class: [
          "border-luce-neutral-border-lowEmphasis",
          "bg-luce-neutral-background-attenuated",
        ].join(" "),
      },
      // The ring, gap and dot fade once, here (Figma 68:9193)
      {
        indicator: "fill",
        disabled: true,
        checked: true,
        class: "opacity-disabled",
      },
      // The check style paints its circle on the inner box, which fills this
      // one; no border here, so the circle is the control's full size and the
      // focus ring wraps it. Its own sizes, with a desktop step (Ardhi's F4
      // answer, 2026-11-02); the fill style keeps `radio-*`
      { indicator: "check", class: "border-0 bg-transparent" },
      {
        indicator: "check",
        size: "sm",
        class: "h-radio-check-sm w-radio-check-sm md:h-radio-check-sm-dt md:w-radio-check-sm-dt",
      },
      {
        indicator: "check",
        size: "md",
        class: "h-radio-check-md w-radio-check-md md:h-radio-check-md-dt md:w-radio-check-md-dt",
      },
      { indicator: "check", disabled: true, checked: true, class: "opacity-disabled" },
    ],
    defaultVariants: {
      size: "md",
      indicator: "fill",
      checked: false,
      disabled: false,
      self: false,
      groupFocus: false,
    },
  },
);

export const radioButtonIndicatorVariants = cva(
  "border-1-5 rounded-radius-full flex items-center justify-center border-solid",
  {
    variants: {
      // Set per style below: the fill style's dot, or the check style's circle
      size: {
        sm: "",
        md: "",
      },
      indicator: {
        fill: "",
        check: "",
      },
      checked: {
        true: "bg-luce-brand-primary border-transparent",
        false: "",
      },
      disabled: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        indicator: "fill",
        size: "sm",
        class: "h-radio-dot-sm w-radio-dot-sm md:h-radio-dot-sm-dt md:w-radio-dot-sm-dt",
      },
      {
        indicator: "fill",
        size: "md",
        class: "h-radio-dot-md w-radio-dot-md md:h-radio-dot-md-dt md:w-radio-dot-md-dt",
      },
      { indicator: "check", class: "size-full" },
      { indicator: "fill", checked: false, class: "border-transparent bg-transparent" },
      {
        indicator: "fill",
        checked: true,
        disabled: false,
        class: "web:group-hover:bg-luce-brand-primaryHover",
      },
      // The check style draws its circle here: white unchecked (Figma 68:9453),
      // the same hover halo, a grey disc when disabled (68:9423)
      {
        indicator: "check",
        checked: false,
        disabled: false,
        class: [
          "border-luce-neutral-border-control",
          "bg-luce-neutral-background-default",
          "web:group-hover:ring-halo web:group-hover:ring-luce-brand-primary/10",
          // The focus ring is on the outer circle; the halo gives way to it
          "web:group-focus-visible:ring-0",
        ].join(" "),
      },
      {
        indicator: "check",
        checked: false,
        disabled: true,
        class: "border-luce-neutral-border-lowEmphasis bg-luce-neutral-background-attenuated",
      },
      {
        indicator: "check",
        checked: true,
        disabled: false,
        class: [
          "web:group-hover:bg-luce-brand-primaryHover",
          "web:group-hover:ring-halo web:group-hover:ring-luce-brand-primary/10",
          "web:group-focus-visible:ring-0",
        ].join(" "),
      },
    ],
    defaultVariants: {
      size: "md",
      indicator: "fill",
      checked: false,
      disabled: false,
    },
  },
);

/**
 * The check style's tick, a Phosphor bold Check: at medium it draws about
 * Figma's 8.25 × 6.6 (68:9443); at small it's larger than Figma's 4.4 × 3.5,
 * which reads as a speck.
 */
export const radioCheckTickSize = { sm: 8, md: 11 } as const;
